import React, { useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Box } from '@mui/material';

// Floating particles component
const FloatingParticles = ({ count = 50, color = 'rgba(244, 114, 22, 0.3)' }) => {
  const particles = useMemo(() => 
    Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 1,
      duration: Math.random() * 20 + 10,
      delay: Math.random() * 5,
    })),
    [count]
  );

  return (
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          style={{
            position: 'absolute',
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
            borderRadius: '50%',
            background: color,
            boxShadow: `0 0 ${particle.size * 2}px ${color}`,
          }}
          animate={{
            y: [0, -30, 0],
            x: [0, Math.random() * 20 - 10, 0],
            opacity: [0.3, 0.8, 0.3],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            delay: particle.delay,
            ease: 'easeInOut',
          }}
        />
      ))}
    </Box>
  );
};

// Animated grid background
const GridBackground = ({ color = 'rgba(244, 114, 22, 0.08)' }) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        backgroundImage: `
          linear-gradient(${color} 1px, transparent 1px),
          linear-gradient(90deg, ${color} 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px',
        maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 70%)',
        WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 70%)',
      }}
    />
  );
};

// Animated gradient orbs
const GradientOrbs = () => {
  return (
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {/* Primary orb */}
      <motion.div
        style={{
          position: 'absolute',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(219, 29, 43, 0.15) 0%, transparent 70%)',
          filter: 'blur(60px)',
          left: '-10%',
          top: '-10%',
        }}
        animate={{
          x: [0, 100, 0],
          y: [0, 50, 0],
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      
      {/* Secondary orb */}
      <motion.div
        style={{
          position: 'absolute',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(244, 114, 22, 0.12) 0%, transparent 70%)',
          filter: 'blur(60px)',
          right: '-5%',
          bottom: '-5%',
        }}
        animate={{
          x: [0, -80, 0],
          y: [0, -60, 0],
          scale: [1, 1.3, 1],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      
      {/* Accent orb */}
      <motion.div
        style={{
          position: 'absolute',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(90, 125, 154, 0.1) 0%, transparent 70%)',
          filter: 'blur(40px)',
          left: '50%',
          top: '30%',
          transform: 'translate(-50%, -50%)',
        }}
        animate={{
          scale: [1, 1.5, 1],
          opacity: [0.5, 0.8, 0.5],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
    </Box>
  );
};

// Circuit lines animation
const CircuitLines = () => {
  const lines = useMemo(() => [
    { x1: '10%', y1: '20%', x2: '30%', y2: '20%', delay: 0 },
    { x1: '30%', y1: '20%', x2: '30%', y2: '40%', delay: 0.5 },
    { x1: '30%', y1: '40%', x2: '50%', y2: '40%', delay: 1 },
    { x1: '70%', y1: '60%', x2: '90%', y2: '60%', delay: 1.5 },
    { x1: '70%', y1: '60%', x2: '70%', y2: '80%', delay: 2 },
    { x1: '50%', y1: '80%', x2: '70%', y2: '80%', delay: 2.5 },
  ], []);

  return (
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      <svg width="100%" height="100%" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(244, 114, 22, 0)" />
            <stop offset="50%" stopColor="rgba(244, 114, 22, 0.5)" />
            <stop offset="100%" stopColor="rgba(244, 114, 22, 0)" />
          </linearGradient>
        </defs>
        {lines.map((line, i) => (
          <motion.line
            key={i}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            stroke="url(#lineGradient)"
            strokeWidth="2"
            strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ 
              pathLength: [0, 1, 0],
              opacity: [0, 0.6, 0],
            }}
            transition={{
              duration: 4,
              delay: line.delay,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        ))}
        
        {/* Circuit nodes */}
        {lines.map((line, i) => (
          <motion.circle
            key={`node-${i}`}
            cx={line.x2}
            cy={line.y2}
            r="4"
            fill="rgba(244, 114, 22, 0.8)"
            initial={{ opacity: 0, scale: 0 }}
            animate={{
              opacity: [0, 1, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: 2,
              delay: line.delay + 1,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        ))}
      </svg>
    </Box>
  );
};

// Data flow animation
const DataFlow = () => {
  const paths = useMemo(() => [
    { d: 'M0,50 Q25,30 50,50 T100,50', delay: 0, color: 'rgba(219, 29, 43, 0.4)' },
    { d: 'M0,30 Q25,50 50,30 T100,30', delay: 1, color: 'rgba(244, 114, 22, 0.4)' },
    { d: 'M0,70 Q25,50 50,70 T100,70', delay: 2, color: 'rgba(90, 125, 154, 0.4)' },
  ], []);

  return (
    <Box 
      sx={{ 
        position: 'absolute', 
        inset: 0, 
        overflow: 'hidden', 
        pointerEvents: 'none',
        opacity: 0.5,
      }}
    >
      <svg 
        viewBox="0 0 100 100" 
        preserveAspectRatio="none" 
        style={{ 
          position: 'absolute', 
          width: '100%', 
          height: '100%' 
        }}
      >
        {paths.map((path, i) => (
          <motion.path
            key={i}
            d={path.d}
            fill="none"
            stroke={path.color}
            strokeWidth="0.5"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{
              duration: 3,
              delay: path.delay,
              repeat: Infinity,
              ease: 'linear',
            }}
          />
        ))}
      </svg>
    </Box>
  );
};

// Main AnimatedBackground component
const AnimatedBackground = ({ 
  variant = 'default', 
  showParticles = true,
  showGrid = true,
  showOrbs = true,
  showCircuits = false,
  showDataFlow = false,
  isDark = false,
}) => {
  const baseColor = isDark ? '#0a0a0f' : '#ffffff';
  const gradientColors = isDark 
    ? 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #16213e 100%)'
    : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 50%, #f0f2f5 100%)';

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        background: gradientColors,
        overflow: 'hidden',
        zIndex: 0,
      }}
    >
      {showOrbs && <GradientOrbs />}
      {showGrid && <GridBackground color={isDark ? 'rgba(244, 114, 22, 0.05)' : 'rgba(244, 114, 22, 0.08)'} />}
      {showParticles && <FloatingParticles count={40} color={isDark ? 'rgba(244, 114, 22, 0.4)' : 'rgba(244, 114, 22, 0.3)'} />}
      {showCircuits && <CircuitLines />}
      {showDataFlow && <DataFlow />}
    </Box>
  );
};

export default AnimatedBackground;
export { FloatingParticles, GridBackground, GradientOrbs, CircuitLines, DataFlow };


